import { Fragment, ReactNode, useEffect, useState } from "react";
import { withRouter } from "react-router-dom";
import { connect } from "react-redux";
import { TextField, Autocomplete, CircularProgress, Checkbox, Grid, Typography } from "@mui/material";
import { getDPList } from "../../../actions/Users/authenticateInventory";
import _ from "lodash";
import './ServiceAccounts.css';

interface Organization {
    id: string;
    name: string;
    parent_of: Organization[];
}

export interface OrgOption {
    id: string;
    name: string;
    level?: number;
    hasChildren?: boolean;
}

export interface OrgSelectionProps {
    authReducer: any;
    label: string;
    value?: OrgOption[];
    disabled?: boolean;
    dpIds?: string[];
    onChange: (orgs: OrgOption[]) => void;
    getDPList: (search?: string) => void;
}

const SELECT_ALL_OPTION: OrgOption = { id: "ALL", name: "Select All", level: 0 };

const OrgSelection = (props: OrgSelectionProps) => {
    const { authReducer, label, value, disabled, onChange, getDPList, dpIds } = props;
    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState<readonly OrgOption[]>([]);

    const createOptionsRecursively = (orgs: Organization[], level = 0, dpIdsSet = new Set(dpIds)): OrgOption[] => {
        const options: OrgOption[] = [];
        orgs.forEach((org) => {
            if (dpIdsSet.has(org.id)) {
                options.push({
                    id: org.id,
                    name: org.name,
                    level: level,
                    hasChildren: org?.parent_of?.length > 0,
                });
            }

            if (org.parent_of.length > 0) {
                options.push(...createOptionsRecursively(org.parent_of, level + 1, dpIdsSet));
            }
        });
        return options;
    };

    useEffect(() => {
        if (open) {
            getDPList();
        }
    }, [open, getDPList]);

    useEffect(() => {
        if (!_.isEmpty(authReducer?.getDPList?.dps)) {
            const uniqueOptions = createOptionsRecursively(authReducer.getDPList.dps);
            setOptions([SELECT_ALL_OPTION, ...uniqueOptions]);
        }
    }, [authReducer.getDPList, dpIds]);

  const renderTags = (selected, getTagProps) => {
    if (selected.length === 0) return null;
    const first = selected[0];
    const extraCount = selected.length - 1;
    const tagProps = getTagProps({ index: 0 });

    return [
      <span {...tagProps} className="org-selection-mul org-selection-mr">
        {first.name}
      </span>,
      ...(extraCount > 0
        ? [
            <span key="extra" className="org-selection-mul">
              +{extraCount}
            </span>,
          ]
        : []),
    ];
  };

  const renderInput = (params) => (
    <TextField
      {...params}
      label={label}
      InputProps={{
        ...params.InputProps,
        endAdornment: (
          <Fragment>
            {authReducer.inventoryListingLoading ? (
              <CircularProgress color="inherit" size={20} />
            ) : null}
            {params.InputProps.endAdornment}
          </Fragment>
        ),
      }}
    />
  );

  const renderOption = (
    props,
    option: OrgOption,
    { selected }
  ): ReactNode => {
    if (option.id === "ALL") {
      const selectedValues = value ?? [];
      const totalOptions = options.filter((o) => o.id !== "ALL").length;

      const allSelected =
        selectedValues.length > 0 && selectedValues.length === totalOptions;

      return (
        <li {...props} key="ALL" className="org-selection-mul-li">
          <Checkbox
            size="small"
            style={{ marginRight: 0 }}
            checked={allSelected}
            indeterminate={
              selectedValues.length > 0 && selectedValues.length < totalOptions
            }
          />
          <Typography component="span" sx={{ marginLeft: "0.5rem" }}>
            {option.name}
          </Typography>
        </li>
      );
    }

    return (
      <li {...props} key={option.id} className="org-selection-mul-li">
        <Grid
          style={{ marginLeft: `${option.level || 0}rem`, paddingLeft: "0.25rem" }}
          className="starlink--base--flex--gap--05x starlink--base--flex--align--center"
        >
          <Checkbox size="small" style={{ marginRight: 0 }} checked={selected} />
          <Grid display={"flex"} alignItems={"center"}>
            {option.level && option.level > 0 ? (
              <>
                <Grid className="org--selection--option--pre0"></Grid>
                <Grid className="org--selection--option--pre1"></Grid>
              </>
            ) : null}
            <Typography component="span" sx={{ marginLeft: "0.5rem" }}>
              {option.name}
            </Typography>
          </Grid>
        </Grid>
      </li>
    );
  };

  return (
    <Autocomplete
      id="select-organization"
      size="small"
      multiple
      fullWidth
      value={value || []}
      disabled={disabled}
      onChange={(event, newValue: any) => {
        const selectedValues = newValue ?? [];
        const totalOptions = options.filter((opt) => opt.id !== "ALL");

        if (selectedValues.some((opt) => opt.id === "ALL")) {
          const allSelected = value?.length === totalOptions.length;

          if (allSelected) {
            onChange([]);
          } else {
            onChange(totalOptions);
          }
        } else {
          onChange(selectedValues);
        }
      }}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      getOptionLabel={(option) => option.name}
      options={options}
      loading={authReducer.inventoryListingLoading}
      renderTags={renderTags}
      renderInput={renderInput}
      renderOption={renderOption}
      disableCloseOnSelect
    />
  );
}

const mapStateToProps = (state) => ({
    authReducer: state.authReducer,
    dpIds: state.starlinkCloud.dpIds,
});

export default withRouter(
    connect(mapStateToProps, {
        getDPList
    })(OrgSelection)
);